// TaskOverviewSurvey.tsx

/* -------------------------------------------------------------------------- */
/*                                   IMPORTS                                  */
/* -------------------------------------------------------------------------- */
import "./TaskOverviewSurvey.scss";
import { useTranslation } from "react-i18next";
import CollapsableContainer from "../../../CollapsableContainer/CollapsableContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faXmark,
    faArrowRight,
} from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import Button3D from "../../../Button3D/Button3D";
import { useEffect, useMemo, useRef, useState } from "react";
import RewardRow from "./components/RewardRow/RewardRow";
import toast, { Toaster } from "react-hot-toast";
import { useSlice } from "@bridge-applications/provider-kit";
import { z } from "zod";
import {
    ApiSurveyRecentCompletionsReqSchema,
    ApiSurveyRecentCompletionsResSchema,
} from "@bridge-applications/eh-types";
import { ExtendedFeedItemSchema } from "../../../../../schemas/slices/FeedSliceSchema";
import { WorldAuthClient } from "@bridge-applications/world-auth";
import { API_BASE } from "../../../../../state/config";
import { walletToGradient } from "../../../../../logic/helpers/walletToGradient";
import Skeleton from "../../../Skeleton/Skeleton";
import RecentCompletionsPreview from "./components/RecentCompletionsPreview/RecentCompletionsPreview";
import ContextualMenuPortal from "../../../ContextualMenuPortal/ContextualMenuPortal";
import { useFloating, offset, shift, autoUpdate } from "@floating-ui/react-dom";

/* -------------------------------------------------------------------------- */
/*                                    TYPES                                   */
/* -------------------------------------------------------------------------- */
type LanguageCode = "en" | "es" | "id" | "pt-br" | "th";
interface TaskOverviewSurveyProps {
    taskId: string;
    onClickStart: (id: string) => void;
    onClose: () => void;
}

/* -------------------------------------------------------------------------- */
/*                                task-overview-survey                               */
/* -------------------------------------------------------------------------- */
export default function TaskOverviewSurvey({
    // task,
    taskId,
    onClickStart,
    onClose,
}: TaskOverviewSurveyProps) {
    const { i18n, t } = useTranslation();
    const lang = i18n.language as LanguageCode;
    /* ---------------------------------- FEED ITEM (SURVEY) ---------------------------------- */
    type ExtendedFeedItem = z.infer<typeof ExtendedFeedItemSchema>;
    type SurveyItem = Extract<ExtendedFeedItem, { productType: "SURVEY" }>;

    const [item, setItem] = useState<SurveyItem>();
    const { state: feedState } = useSlice("feedSlice");
    useEffect(() => {
        const found = feedState.items.find(
            (i): i is SurveyItem =>
                i.productType === "SURVEY" && i.itemId === taskId,
        );
        if (found) setItem(found);
    }, [taskId, feedState.items]);

    // Convenience handles to the SURVEY raw doc and relation (if present)
    console.log("item", item);
    const survey = item?.raw; // typed as SurveySchema from the union
    const relation = item?.relation; // typed as UserRelationWithSurvey | undefined

    // Derive localized content
    const titleText =
        (typeof survey?.title === "object"
            ? survey?.title?.[lang]
            : undefined) ??
        (typeof survey?.title === "object" ? survey?.title?.en : undefined) ??
        (typeof survey?.title === "string" ? survey?.title : "") ??
        item?.title ??
        "Untitled";

    console.log("survey", survey);
    const descText =
        (typeof survey?.desc === "object" ? survey?.desc?.[lang] : undefined) ??
        (typeof survey?.desc === "object" ? survey?.desc?.en : undefined) ??
        (typeof survey?.desc === "string" ? survey?.desc : "") ??
        "";

    // Rewards: normalize items to { unit, amount, isPrimary } and split
    const { primaryReward, secondaryRewards } = useMemo(() => {
        // 1) Flatten rewards[] regardless of shape (array vs. {items:[]}, nested reward vs. top-level)
        const raw = Array.isArray(survey?.rewards)
            ? (survey?.rewards as any[])
            : survey?.rewards &&
                typeof survey.rewards === "object" &&
                "items" in (survey.rewards as any)
              ? ((survey?.rewards as any).items as any[])
              : [];

        const flat: Array<{
            unit: string;
            amount: number;
            isPrimary: boolean;
        }> = (raw ?? [])
            .map((r: any) => {
                const unit = r.unit ?? r.reward?.unit ?? null;
                const amount = r.amount ?? r.reward?.amount ?? null;
                const isPrimary = Boolean(r.isPrimaryReward);
                return unit != null && amount != null
                    ? { unit: String(unit), amount: Number(amount), isPrimary }
                    : null;
            })
            .filter(Boolean) as any[];

        // 2) Choose primary: prefer flagged; else fall back to survey primary_* or the item-level fallback
        const primary =
            flat.find((r) => r.isPrimary) ??
            (survey?.primary_reward_unit != null &&
            survey?.primary_reward_amount != null
                ? {
                      unit: String(survey.primary_reward_unit),
                      amount: Number(survey.primary_reward_amount),
                      isPrimary: true,
                  }
                : item?.rewardUnit != null && item?.rewardAmount != null
                  ? {
                        unit: String(item.rewardUnit),
                        amount: Number(item.rewardAmount),
                        isPrimary: true,
                    }
                  : null);

        // 3) Secondary = all others not matching the chosen primary (by unit+amount)
        const secondaries = flat.filter(
            (r) =>
                !(
                    primary &&
                    r.unit === primary.unit &&
                    Number(r.amount) === Number(primary.amount)
                ),
        );

        return {
            primaryReward: primary
                ? { unit: primary.unit, amount: primary.amount }
                : undefined,
            secondaryRewards: secondaries.map((r) => ({
                unit: r.unit,
                amount: r.amount,
            })),
        };
    }, [survey, item?.rewardUnit, item?.rewardAmount]);

    /* -------------------------------------------------------------------------- */
    /*                          RECENT COMPLETIONS (Redis)                        */
    /* -------------------------------------------------------------------------- */
    type RecentCompletion = z.infer<
        typeof ApiSurveyRecentCompletionsResSchema
    >["recentCompletions"][number];
    const [recentCompletions, setRecentCompletions] = useState<
        RecentCompletion[]
    >([]);
    const [totalCompletionsCount, setTotalCompletionsCount] =
        useState<number>(0);
    const [recentCompletionsLoading, setRecentCompletionsLoading] =
        useState<boolean>(true);

    // menu state + positioning
    const [menuOpen, setMenuOpen] = useState(false);
    const recentCompletionsBtnRef = useRef<HTMLButtonElement>(null);
    const {
        x,
        y,
        strategy,
        refs: { setReference, setFloating },
    } = useFloating({
        placement: "bottom-start",
        middleware: [
            offset(({ rects }) => ({
                mainAxis: -rects.reference.height,
                crossAxis: 0,
            })),
            shift({ padding: 5 }),
        ],
        whileElementsMounted: autoUpdate,
    });

    // set anchor for floating menu once the button exists
    useEffect(() => {
        if (recentCompletionsBtnRef.current) {
            requestAnimationFrame(() => {
                if (recentCompletionsBtnRef.current) {
                    setReference(recentCompletionsBtnRef.current);
                }
            });
        }
    }, [setReference, recentCompletions]);

    const handleRecentCompletionsBtnClick = () => {
        if (recentCompletionsBtnRef.current) {
            const rect =
                recentCompletionsBtnRef.current.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
                setMenuOpen(true);
            } else {
                requestAnimationFrame(() => setMenuOpen(true));
            }
        }
    };

    useEffect(() => {
        if (!item?.itemId || item?.productType !== "SURVEY") return;
        let cancelled = false;
        (async () => {
            try {
                setRecentCompletionsLoading(true);
                const { accessToken } =
                    await WorldAuthClient.getAccessTokenSilently();
                // Matches worker route for recent completions
                const RECENTS_ENDPOINT = `${API_BASE}/api/v1/survey/recent-completions`;
                const payload: z.infer<
                    typeof ApiSurveyRecentCompletionsReqSchema
                    // > = { surveyId: item.itemId, limit: 10 };
                > = { itemType: "SURVEY", itemId: item.itemId, limit: 10 };
                const res = await fetch(RECENTS_ENDPOINT, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload),
                    credentials: "include",
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const parsed =
                    ApiSurveyRecentCompletionsResSchema.safeParse(data);
                if (!parsed.success || parsed.data.status !== "OK") {
                    throw new Error("Invalid recent-completions payload");
                }
                if (!cancelled) {
                    setRecentCompletions(parsed.data.recentCompletions);
                    setTotalCompletionsCount(parsed.data.totalCompletionsCount);
                    setRecentCompletionsLoading(false);
                }
            } catch (e) {
                if (!cancelled) {
                    setRecentCompletions([]);
                    setTotalCompletionsCount(0);
                    setRecentCompletionsLoading(false);
                    // non-fatal; keep UI running
                    console.warn("recent-completions fetch failed:", e);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [item?.itemId, item?.productType]);

    if (!item) {
        return null;
    }

    return (
        <>
            <div className="task-overview-survey">
                <div className="task-overview-survey__header">
                    <h3 className="task-overview-survey__header__title">
                        {titleText}
                    </h3>
                    <div className="task-overview-survey__header__subtitle">
                        {`${
                            item.productType === "SURVEY"
                                ? t("TaskOverviewSurvey.primaryTypeSurvey")
                                : t("TaskOverviewSurvey.primaryTypeActivity")
                        }  •  ${item.spotsLeft ?? ""} ${t(
                            "TaskOverviewSurvey.spotsLeft",
                        )}  •  ${Math.round(
                            Number(survey?.est_completion_time ?? 0) / 60000,
                        )} ${t("TaskOverviewSurvey.minAbbreviation")} (${t(
                            "TaskOverviewSurvey.estDurationShort",
                        )})`}
                    </div>
                </div>

                <button
                    className="task-overview-survey__close-btn"
                    onClick={onClose}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                {survey?.creator_logo_url && survey?.creator_name && (
                    <div className="task-overview-survey__task-creator">
                        <img
                            src={survey.creator_logo_url}
                            alt={
                                (typeof survey.creator_name === "object"
                                    ? survey.creator_name?.[lang]
                                    : undefined) ??
                                (typeof survey.creator_name === "object"
                                    ? survey.creator_name?.en
                                    : undefined) ??
                                (typeof survey.creator_name === "string"
                                    ? survey.creator_name
                                    : "") ??
                                "Creator"
                            }
                            onTouchStart={(e) => {
                                // Prevent iOS long-press behavior
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onContextMenu={(e) => {
                                // Prevent context menu on all platforms
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onMouseDown={(e) => {
                                // Prevent mouse-based context menu
                                e.preventDefault();
                            }}
                            draggable={false}
                            style={{
                                touchAction: "none",
                                WebkitTouchCallout: "none",
                                WebkitUserSelect: "none",
                                userSelect: "none",
                            }}
                        />
                        <p>
                            {(typeof survey.creator_name === "object"
                                ? survey.creator_name?.[lang]
                                : undefined) ??
                                (typeof survey.creator_name === "object"
                                    ? survey.creator_name?.en
                                    : undefined) ??
                                (typeof survey.creator_name === "string"
                                    ? survey.creator_name
                                    : "") ??
                                ""}
                        </p>
                    </div>
                )}

                {/* --------------------- RECENT COMPLETIONS (avatars + menu) --------------------- */}
                {recentCompletions.length > 6 && (
                    <>
                        {recentCompletionsLoading ? (
                            <div className="task-overview-survey__recent-completions-loading">
                                <Skeleton
                                    baseColor="#BABABA"
                                    highlightVariation={35}
                                    borderRadius={"9999px"}
                                    height={"5.23svh"}
                                    width={"55.45vw"}
                                    opacity={0.25}
                                />
                            </div>
                        ) : (
                            <button
                                className="task-overview-survey__recent-completions"
                                onClick={handleRecentCompletionsBtnClick}
                                ref={recentCompletionsBtnRef}
                            >
                                <div className="task-overview-survey__recent-completions__items">
                                    {recentCompletions
                                        .slice(0, 5)
                                        .map(
                                            (
                                                c: RecentCompletion,
                                                i: number,
                                                arr: RecentCompletion[],
                                            ) => {
                                                const [topLeft, bottomRight] =
                                                    walletToGradient(
                                                        c.walletAddress,
                                                    );
                                                const baseZIndex = 10;
                                                const zIndex =
                                                    baseZIndex +
                                                    (arr.length - 1 - i);
                                                return (
                                                    <div
                                                        className={`task-overview-survey__recent-completions__item ${
                                                            i === 0
                                                                ? "first"
                                                                : ""
                                                        }`}
                                                        key={`${c.walletAddress}-${i}`}
                                                        style={{
                                                            background: `linear-gradient(135deg, ${topLeft} 0%, ${bottomRight} 100%)`,
                                                            zIndex,
                                                        }}
                                                    >
                                                        <p>
                                                            {(
                                                                c
                                                                    .username?.[0] ??
                                                                "?"
                                                            ).toUpperCase()}
                                                        </p>
                                                    </div>
                                                );
                                            },
                                        )}
                                </div>
                                <p className="task-overview-survey__recent-completions__extra">
                                    +
                                    {Math.max(
                                        0,
                                        (totalCompletionsCount || 0) -
                                            Math.min(
                                                5,
                                                recentCompletions.length,
                                            ),
                                    )}
                                </p>
                            </button>
                        )}
                    </>
                )}

                <div className="task-overview-survey__desc">
                    <CollapsableContainer
                        maxHeight={"20svh"}
                        minHeight={"12.27svh"}
                    >
                        <p>{descText}</p>
                    </CollapsableContainer>
                </div>

                {/* Requirements section kept guarded — render if present on raw */}
                {survey?.requirements && (
                    <div className="task-overview-survey__requirements">
                        <CollapsableContainer
                            maxHeight={"25svh"}
                            minHeight={"12.27svh"}
                            label={"Requirements"}
                        >
                            <p>
                                Proficient in one of the languages:
                                <br />
                                - Spanish
                                <br />- English
                            </p>
                        </CollapsableContainer>
                    </div>
                )}

                <div className="task-overview-survey__rewards">
                    <div className="task-overview-survey__rewards__header">
                        <div className="task-overview-survey__rewards__header__inner">
                            <p>{t("TaskOverviewSurvey.rewards")}</p>
                        </div>
                    </div>
                    <div className="task-overview-survey__rewards__container">
                        {/* 3. Render the primary reward if it exists */}
                        {primaryReward && (
                            <RewardRow
                                reward={primaryReward}
                                isPrimary
                                isFirst
                                isLast={(secondaryRewards?.length ?? 0) === 0}
                            />
                        )}

                        {/* 4. Map over and render all secondary rewards */}
                        {secondaryRewards?.map((rewardItem, index) => (
                            <RewardRow
                                key={`${String(rewardItem.unit)}-${index}`}
                                reward={{
                                    unit: String(rewardItem.unit),
                                    amount: Number(rewardItem.amount),
                                }}
                                isFirst={!primaryReward && index === 0}
                                isLast={
                                    index ===
                                    (secondaryRewards?.length ?? 0) - 1
                                }
                            />
                        ))}
                    </div>
                </div>

                <div
                    className={`task-overview-survey__start-btn ${
                        !item.spotsLeft || item.spotsLeft === 0
                            ? "no-spots-left"
                            : ""
                    } ${
                        relation?.status === "COMPLETED" ? "task-completed" : ""
                    }`}
                >
                    <Button3D
                        onClick={() => {
                            if (!item.spotsLeft || item.spotsLeft === 0) {
                                toast.error(
                                    t("TaskOverviewSurvey.noSpotsLeft"),
                                );
                                return;
                            } else if (relation?.status === "COMPLETED") {
                                toast.error(
                                    t(
                                        "TaskOverviewSurvey.taskAlreadyCompleted",
                                    ),
                                );
                                return;
                            } else if (
                                relation &&
                                !relation.canStart &&
                                !relation.canResume
                            ) {
                                toast.error(
                                    t("TaskOverviewSurvey.noPermissionToStart"),
                                );
                                return;
                            }
                            onClickStart(item.itemId);
                        }}
                        className={`task-overview-survey__start-3d-btn ${
                            !item.spotsLeft || item.spotsLeft === 0
                                ? "disabled"
                                : ""
                        }`}
                        // className={`task-overview-survey__start-3d-btn`}
                    >
                        <div className="task-overview-survey__start-btn__content">
                            <p>
                                {t("TaskOverviewSurvey.start")}
                                <span>
                                    {`(${Math.round(
                                        Number(
                                            survey?.est_completion_time ?? 0,
                                        ) / 60000,
                                    )} min.)`}
                                </span>
                            </p>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </div>
                    </Button3D>
                    <p className="task-overview-survey__start-btn__tac">
                        {t("TaskOverviewSurvey.tac")} {item.itemId}
                    </p>
                </div>

                {/* SVG-GOLD-MASK */}
                <svg width="0" height="0" style={{ position: "absolute" }}>
                    <defs>
                        <linearGradient
                            id="grad-gold-2"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop offset="0%" stopColor="#F0A000" />
                            <stop offset="100%" stopColor="#D08B00" />
                        </linearGradient>
                    </defs>
                </svg>
            </div>
            {/* Floating menu with top 5 preview */}
            <ContextualMenuPortal
                isOpen={menuOpen}
                onClose={() => setMenuOpen(false)}
                floatingRef={setFloating}
                x={x}
                y={y}
                strategy={strategy}
            >
                <RecentCompletionsPreview
                    recentCompletions={recentCompletions.slice(0, 5)}
                    totalCompletionsCount={totalCompletionsCount}
                />
            </ContextualMenuPortal>
            <Toaster />
        </>
    );
}
